import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import { fetchGroupAIUsage } from 'slices/dchatSlice';
import {
  formatUsd,
  recordsForMonth,
  summarizeUpstreamCost,
} from './aiUsageCost';

export default function GroupAIUsage({ group_guid, group_name }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default to current month
  const reduxDispatch = useDispatch();

  const reduxCurrentlySelectedGroup = useSelector(
    (state) => state.app.currentlySelectedGroup
  );
  const isFetchGroupAIUsageLoaded = useSelector(
    (state) => state.dchat.isFetchGroupAIUsageLoaded
  );
  const reduxGroupAIUsage = useSelector((state) => {
    const usage = state.dchat.groupAIUsage;
    const gg = group_guid || reduxCurrentlySelectedGroup?.group_guid;
    return usage ? usage[gg] : null;
  });

  useEffect(() => {
    if (reduxCurrentlySelectedGroup && reduxCurrentlySelectedGroup.group_guid) {
      reduxDispatch(
        fetchGroupAIUsage({
          group_guid:
            (!!group_guid && group_guid) ||
            reduxCurrentlySelectedGroup.group_guid,
        })
      );
    }
  }, [reduxCurrentlySelectedGroup, group_guid, reduxDispatch]);

  const daysInMonth = (year, month) => new Date(year, month, 0).getDate();
  const getMonthName = (month) =>
    new Date(0, month - 1).toLocaleString('en-US', { month: 'long' });

  let option;
  const aggregatedData = {};
  let chartData;
  if (reduxGroupAIUsage) {
    const days = daysInMonth(selectedYear, selectedMonth);
    const monthName = getMonthName(selectedMonth);

    // Initialize all days in the month
    for (let i = 1; i <= days; i++) {
      const day = String(i);
      aggregatedData[`${monthName} ${day}`] = {
        prompt_tokens: 0,
        completion_tokens: 0,
        records: [],
      };
    }

    // Aggregate data
    recordsForMonth(reduxGroupAIUsage, selectedYear, selectedMonth).forEach(
      (item) => {
        const key = `${monthName} ${new Date(item.timestamp).getDate()}`;
        if (aggregatedData[key] !== undefined) {
          aggregatedData[key].prompt_tokens += item.prompt_tokens;
          aggregatedData[key].completion_tokens += item.completion_tokens;
          aggregatedData[key].records.push(item);
        }
      }
    );

    // Prepare data for the chart. Cost is the upstream charge recorded per call (see aiUsageCost.ts).
    chartData = Object.keys(aggregatedData).map((date) => ({
      date: date,
      prompt_tokens: aggregatedData[date].prompt_tokens,
      completion_tokens: aggregatedData[date].completion_tokens,
      cost: summarizeUpstreamCost(aggregatedData[date].records),
    }));

    // ECharts configuration
    option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params) => {
          const promptData = params.find(
            (param) => param.seriesName === 'Prompt Tokens'
          );
          const completionData = params.find(
            (param) => param.seriesName === 'Completion Tokens'
          );
          const { cost } = promptData.data;
          return `${promptData.data.date}<br/>
                  Prompt Tokens: ${promptData.data.value}<br/>
                  Completion Tokens: ${completionData.data.value}<br/>
                  AI Model Cost: ${formatUsd(cost.modelCost)}<br/>
                  Data Services Cost: ${formatUsd(cost.dataServiceCost)}<br/>
                  Total Cost: ${formatUsd(cost.total)}${cost.includesEstimates ? ' (includes estimates)' : ''}`;
        },
      },
      legend: {
        data: ['Prompt Tokens', 'Completion Tokens'],
        textStyle: {
          color: '#ffffff', // Set text color to white
        },
      },
      xAxis: {
        type: 'category',
        data: chartData.map((item) => item.date),
      },
      yAxis: {
        type: 'value',
        name: 'Tokens',
      },
      series: [
        {
          name: 'Prompt Tokens',
          data: chartData.map((item) => ({
            date: item.date,
            value: item.prompt_tokens,
            cost: item.cost,
          })),
          type: 'bar',
          stack: 'tokens',
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)',
          },
        },
        {
          name: 'Completion Tokens',
          data: chartData.map((item) => ({
            date: item.date,
            value: item.completion_tokens,
          })),
          type: 'bar',
          stack: 'tokens',
        },
      ],
    };
  }

  const monthCost = summarizeUpstreamCost(
    reduxGroupAIUsage
      ? recordsForMonth(reduxGroupAIUsage, selectedYear, selectedMonth)
      : []
  );

  return (
    <>
      <h4>
        PRATUS AI Services Usage for:{' '}
        {group_name || reduxCurrentlySelectedGroup.group_name}
      </h4>
      <h5>
        {getMonthName(selectedMonth)} {selectedYear} TOTAL:{' '}
        {formatUsd(monthCost.total)}
      </h5>
      <p>
        PRATUS AI Services usage is billed at cost: the amounts shown are what
        the AI model provider (Azure OpenAI) and any external data services
        charged for each request, passed through without markup. Input tokens
        include data sources for PRATUS AI SITREP and PRATUS AI Agent.
      </p>
      {monthCost.includesEstimates && (
        <p>
          Some of this month&apos;s usage predates per-request cost tracking and
          is estimated from the model&apos;s list price.
        </p>
      )}
      {monthCost.unpricedTokens > 0 && (
        <p>
          {monthCost.unpricedTokens.toLocaleString()} tokens this month predate
          cost tracking and have no recorded cost; they are not included in the
          total.
        </p>
      )}
      <label>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
            <option key={month} value={month}>
              {getMonthName(month)}
            </option>
          ))}
        </select>
      </label>
      <label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {Array.from(
            { length: new Date().getFullYear() - 2023 },
            (_, i) => 2024 + i
          ).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </label>
      {(!isFetchGroupAIUsageLoaded && (
        <div>
          Loading AI Usage...<i className="fa fa-spinner fa-pulse"></i>
        </div>
      )) || (
        <>
          {reduxGroupAIUsage && option && (
            <div className="form-block">
              <ReactECharts option={option} style={{ height: 400 }} />
            </div>
          )}
        </>
      )}
    </>
  );
}
